import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid2 as Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";

const InsuranceRatesTable = ({ rateData }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!rateData) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
        <Typography color="text.secondary">
          No rate data available for display.
        </Typography>
      </Paper>
    );
  }

  const { healthRates = [], dentalRates = [], visionRates = [] } = rateData;

  // Transform flat HealthRates List into an Age Matrix: { age, BRONZE, SILVER, PLATINUM }
  const healthMatrixMap = healthRates.reduce((acc, curr) => {
    if (!acc[curr.age]) {
      acc[curr.age] = { age: curr.age };
    }
    acc[curr.age][curr.planType] = curr.monthlyCost;
    return acc;
  }, {});

  const healthMatrixRows = Object.values(healthMatrixMap).sort(
    (a, b) => a.age - b.age,
  );

  const formatCurrency = (val) =>
    val != null ? `$${Number(val).toFixed(2)}` : "—";

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {/* Navigation Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 3, boxShadow: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            icon={<LocalHospitalIcon />}
            iconPosition="start"
            label={`Health Rates (${healthMatrixRows.length} Ages)`}
          />
          <Tab
            icon={<MedicalServicesIcon />}
            iconPosition="start"
            label={`Dental Rates (${dentalRates.length} Tiers)`}
          />
          <Tab
            icon={<VisibilityIcon />}
            iconPosition="start"
            label={`Vision Rates (${visionRates.length} Tiers)`}
          />
        </Tabs>
      </Paper>
      {/* TAB 1: HEALTH RATES (MATRIX TABLE) */}
      {activeTab === 0 && (
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden", 
            position: "relative",
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 600,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Table
              stickyHeader
              aria-label="health rate matrix"
              sx={{ borderCollapse: "separate" }}
            >
              <TableHead>
                <TableRow>
                  {[
                    {
                      label: "Age Bracket",
                      align: "left",
                      color: "text.primary",
                    },
                    { label: "Bronze Plan", align: "right", color: "#cd7f32" },
                    { label: "Silver Plan", align: "right", color: "#718096" },
                    {
                      label: "Platinum Plan",
                      align: "right",
                      color: "#4a5568",
                    },
                  ].map((col, idx) => (
                    <TableCell
                      key={idx}
                      align={col.align}
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.95rem",
                        color: col.color,
                        backgroundColor: "background.paper",
                        backgroundImage:
                          "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))", // Retains MUI paper elevation tint
                        top: 0,
                        zIndex: 3,
                        borderBottom: "2px solid",
                        borderColor: "divider",
                        paddingTop: "16px",
                        paddingBottom: "16px",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {healthMatrixRows.map((row, index) => {
                  const isFirst = index === 0;
                  const isLast = index === healthMatrixRows.length - 1;

                  let ageLabel = `${row.age} yrs`;
                  if (isFirst) {
                    ageLabel = `0–${row.age} yrs`;
                  } else if (isLast) {
                    ageLabel = `${row.age}+ yrs`;
                  }

                  return (
                    <TableRow key={row.age} hover>
                      <TableCell>
                        <Chip
                          label={ageLabel}
                          size="small"
                          color={isFirst || isLast ? "primary" : "default"}
                          variant={isFirst || isLast ? "filled" : "outlined"}
                          sx={{ fontWeight: "bold" }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "medium" }}>
                        {formatCurrency(row.BRONZE)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "medium" }}>
                        {formatCurrency(row.SILVER)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "medium"}}
                      >
                        {formatCurrency(row.PLATINUM)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}{" "}\

      {/* TAB 2: DENTAL RATES (TIER CARDS & TABLE) */}
      {activeTab === 1 && (
        <Grid container spacing={2.5}>
          {dentalRates.map((item, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: 1 }}>
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight="bold"
                  >
                    {item.tier?.replace("_", " ") || "COVERAGE TIER"}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(item.monthlyCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Per month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      {/* TAB 3: VISION RATES (TIER CARDS & TABLE) */}
      {activeTab === 2 && (
        <Grid container spacing={2.5}>
          {visionRates.map((item, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: 1 }}>
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight="bold"
                  >
                    {item.tier?.replace("_", " ") || "COVERAGE TIER"}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    {formatCurrency(item.monthlyCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Per month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default InsuranceRatesTable;
